import { useState, useEffect, useRef } from "react";
import { BarChart3, Clock, Target, TrendingDown, TrendingUp, AlertTriangle, Users, Zap, Download, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = "bg-[#D6E6FA]" }) => (
  <div className={`brutalist-card p-5 ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon className="w-5 h-5" />
      <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">{label}</span>
    </div>
    <div className="text-2xl font-extrabold">{value}</div>
    {sub && <div className="text-xs text-[#4A4A4A] mt-1">{sub}</div>}
  </div>
);

const ProgressBar = ({ value, max = 100, color = "bg-[#3C8DE4]", label }) => (
  <div className="flex items-center gap-3">
    {label && <span className="text-xs w-16 text-right font-medium shrink-0">{label}</span>}
    <div className="flex-1 h-4 bg-[#F0EFEB] rounded-full overflow-hidden border border-[#0A0A0A]">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, max)}%` }} />
    </div>
    <span className="text-xs font-bold w-10">{value}%</span>
  </div>
);

const DIFF_COLORS = {
  easy: { bg: "bg-[#D6E6FA]", bar: "bg-[#3C8DE4]", text: "text-[#1B5EA0]" },
  medium: { bg: "bg-[#FBE7C6]", bar: "bg-[#E8A838]", text: "text-[#8B5E00]" },
  hard: { bg: "bg-[#FFAEBC]", bar: "bg-[#E85A6E]", text: "text-[#8B0000]" },
};

const TYPE_LABELS = {
  mcq: "Multiple Choice",
  fill_blank: "Fill in Blank",
  translation: "Translation",
  listening: "Listening",
};

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [anonData, setAnonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [tab, setTab] = useState("overview");
  const [filterOptions, setFilterOptions] = useState({ languages: [], courses: [] });
  const [languageId, setLanguageId] = useState("");
  const [courseId, setCourseId] = useState("");
  const didMount = useRef(false);

  useEffect(() => {
    loadFilterOptions();
    loadAnalytics(true);
  }, []);

  // Re-fetch quiz results analytics whenever a filter changes — no page reload,
  // and keep the dropdowns + existing data visible while the new data loads.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    loadAnalytics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageId, courseId]);

  const loadFilterOptions = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/analytics/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilterOptions(res.data);
    } catch {
      // Filters are a non-critical enhancement — fail silently
    }
  };

  const loadAnalytics = async (isInitial) => {
    if (isInitial) setLoading(true);
    else setFiltering(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const params = {};
      if (languageId) params.language_id = languageId;
      if (courseId) params.course_id = courseId;
      const [assignRes, anonRes] = await Promise.all([
        axios.get(`${API}/analytics/assignments`, { headers, params }),
        axios.get(`${API}/analytics/data-collection`, { headers }).catch(() => ({ data: null })),
      ]);
      setData(assignRes.data);
      setAnonData(anonRes.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setFiltering(false);
    }
  };

  // Only show course/level options that belong to the selected language
  const visibleCourses = languageId
    ? filterOptions.courses.filter((c) => c.language_id === languageId)
    : filterOptions.courses;

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguageId(newLang);
    // Reset the level filter if it no longer belongs to the newly selected language
    if (newLang && courseId) {
      const stillValid = filterOptions.courses.some((c) => c.id === courseId && c.language_id === newLang);
      if (!stillValid) setCourseId("");
    }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/analytics/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "virtually_verbal_analytics.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported! Open it in Google Sheets via File > Import");
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  const filterBar = (
    <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="quiz-results-filter-bar">
      <select
        value={languageId}
        onChange={handleLanguageChange}
        className="brutalist-input text-sm !py-2 !px-3 w-auto"
        data-testid="filter-language-select"
      >
        <option value="">All Languages</option>
        {filterOptions.languages.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>
      <select
        value={courseId}
        onChange={(e) => setCourseId(e.target.value)}
        className="brutalist-input text-sm !py-2 !px-3 w-auto"
        data-testid="filter-level-select"
      >
        <option value="">All Levels</option>
        {visibleCourses.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      {filtering && <span className="text-xs text-[#4A4A4A] italic">Updating...</span>}
      {(languageId || courseId) && (
        <button
          onClick={() => { setLanguageId(""); setCourseId(""); }}
          className="text-xs font-bold text-[#3C8DE4] hover:underline"
          data-testid="clear-filters-btn"
        >
          Clear filters
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!data || data.total_submissions === 0) {
    return (
      <div>
        <h1 className="text-3xl font-bold mb-2" data-testid="analytics-title">Assignment Analytics</h1>
        <p className="text-[#4A4A4A] mb-8">Data insights on student performance</p>
        {filterBar}
        <div className="brutalist-card p-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
          <h2 className="text-xl font-bold mb-2">No Data Yet</h2>
          <p className="text-[#4A4A4A]">
            {languageId || courseId
              ? "No quiz results match this filter. Try a different language or level."
              : "Analytics will appear once students start submitting assignments."}
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "difficulty", label: "Difficulty Analysis" },
    { id: "questions", label: "Question Insights" },
    { id: "mistakes", label: "Common Mistakes" },
    { id: "data", label: "Data Insights" },
  ];

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-3xl font-bold" data-testid="analytics-title">Assignment Analytics</h1>
        <button
          onClick={exportCSV}
          data-testid="export-csv-btn"
          className="brutalist-btn-secondary flex items-center gap-2 text-sm !px-4 !py-2"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <p className="text-[#4A4A4A] mb-6">Data insights on student performance and question difficulty</p>

      {filterBar}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="analytics-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-bold border-2 border-[#0A0A0A] transition-colors ${
              tab === t.id ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F0EFEB]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={data} />}
      {tab === "difficulty" && <DifficultyTab data={data} />}
      {tab === "questions" && <QuestionsTab data={data} />}
      {tab === "mistakes" && <MistakesTab data={data} />}
      {tab === "data" && <DataInsightsTab data={anonData} />}
    </div>
  );
};

const OverviewTab = ({ data }) => {
  const typePopularity = data.type_popularity || {};
  const totalByType = Object.values(typePopularity).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Zap} label="Total Submissions" value={data.total_submissions} />
        <StatCard icon={Target} label="Overall Accuracy" value={`${data.overall_accuracy}%`} color="bg-[#FBE7C6]" />
        <StatCard
          icon={Clock}
          label="Avg Time"
          value={data.avg_time_seconds > 0 ? `${data.avg_time_seconds}s` : "N/A"}
          sub={data.avg_time_seconds > 0 ? "per question" : "No time data yet"}
          color="bg-[#FFAEBC]"
        />
        <StatCard
          icon={Users}
          label="Active Weeks"
          value={Object.keys(data.weekly_completion || {}).length}
          sub="with submissions"
        />
      </div>

      {/* Type Preference & Engagement */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Student Preferences by Question Type
          </h3>
          <div className="space-y-3">
            {Object.entries(typePopularity)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => {
                const pct = totalByType > 0 ? Math.round((count / totalByType) * 100) : 0;
                const typeInfo = data.time_by_type?.[type] || {};
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{TYPE_LABELS[type] || type}</span>
                      <span className="text-[#4A4A4A]">
                        {count} attempts ({pct}%) &middot; {typeInfo.accuracy || 0}% accuracy
                      </span>
                    </div>
                    <ProgressBar value={pct} color="bg-[#3C8DE4]" />
                  </div>
                );
              })}
          </div>
        </div>

        {/* Time by Type */}
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Avg Time by Question Type
          </h3>
          <div className="space-y-3">
            {Object.entries(data.time_by_type || {})
              .sort((a, b) => b[1].avg_seconds - a[1].avg_seconds)
              .map(([type, info]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-[#F0EFEB] rounded-lg">
                  <span className="font-medium text-sm">{TYPE_LABELS[type] || type}</span>
                  <div className="text-right">
                    <span className="font-bold text-sm">{info.avg_seconds > 0 ? `${info.avg_seconds}s` : "N/A"}</span>
                    <span className="text-xs text-[#4A4A4A] ml-2">({info.total_attempts} attempts)</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Weekly Engagement */}
      <div className="brutalist-card p-5">
        <h3 className="font-bold text-base mb-4">Weekly Engagement</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => {
            const ws = data.weekly_completion?.[String(w)];
            return (
              <div
                key={w}
                data-testid={`week-${w}-analytics`}
                className={`p-3 rounded-lg border-2 border-[#0A0A0A] text-center ${
                  ws ? "bg-[#D6E6FA]" : "bg-[#F0EFEB]"
                }`}
              >
                <div className="text-[10px] text-[#4A4A4A]">Week {w}</div>
                <div className="font-bold text-lg">{ws ? ws.total_attempts : 0}</div>
                <div className="text-[10px]">
                  {ws ? `${ws.accuracy}% acc` : "No data"}
                </div>
                {ws && ws.avg_time > 0 && <div className="text-[10px] text-[#4A4A4A]">{ws.avg_time}s avg</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DifficultyTab = ({ data }) => (
  <div className="space-y-6">
    {/* Difficulty Stats */}
    <div className="grid sm:grid-cols-3 gap-4">
      {["easy", "medium", "hard"].map((diff) => {
        const info = data.time_by_difficulty?.[diff] || { avg_seconds: 0, total_attempts: 0, accuracy: 0 };
        const dc = DIFF_COLORS[diff];
        return (
          <div key={diff} className={`brutalist-card p-5 ${dc.bg}`} data-testid={`difficulty-${diff}-stats`}>
            <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${dc.text}`}>
              {diff.charAt(0).toUpperCase() + diff.slice(1)}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Accuracy</span>
                <span className="font-bold text-sm">{info.accuracy}%</span>
              </div>
              <ProgressBar value={info.accuracy} color={dc.bar} />
              <div className="flex justify-between text-xs text-[#4A4A4A] pt-1">
                <span>{info.total_attempts} attempts</span>
                <span>{info.avg_seconds > 0 ? `${info.avg_seconds}s avg` : "No time data"}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Difficulty Distribution per Week */}
    <div className="brutalist-card p-5">
      <h3 className="font-bold text-base mb-4 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" /> Student Difficulty Level per Week
      </h3>
      <p className="text-xs text-[#4A4A4A] mb-4">How many students are at each difficulty tier per week</p>
      {Object.keys(data.difficulty_distribution || {}).length > 0 ? (
        <div className="space-y-3">
          {Object.entries(data.difficulty_distribution)
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([week, dist]) => {
              const total = dist.easy + dist.medium + dist.hard;
              return (
                <div key={week} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-16 shrink-0">Week {week}</span>
                  <div className="flex-1 flex h-6 rounded-full overflow-hidden border border-[#0A0A0A]">
                    {total > 0 && (
                      <>
                        <div
                          className="bg-[#3C8DE4] flex items-center justify-center text-[10px] text-white font-bold"
                          style={{ width: `${(dist.easy / total) * 100}%` }}
                        >
                          {dist.easy > 0 && dist.easy}
                        </div>
                        <div
                          className="bg-[#E8A838] flex items-center justify-center text-[10px] text-white font-bold"
                          style={{ width: `${(dist.medium / total) * 100}%` }}
                        >
                          {dist.medium > 0 && dist.medium}
                        </div>
                        <div
                          className="bg-[#E85A6E] flex items-center justify-center text-[10px] text-white font-bold"
                          style={{ width: `${(dist.hard / total) * 100}%` }}
                        >
                          {dist.hard > 0 && dist.hard}
                        </div>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-[#4A4A4A] w-10">{total} students</span>
                </div>
              );
            })}
          <div className="flex gap-4 mt-2 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#3C8DE4]" /> Easy</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#E8A838]" /> Medium</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#E85A6E]" /> Hard</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#4A4A4A]">No difficulty distribution data yet.</p>
      )}
    </div>
  </div>
);

const QuestionsTab = ({ data }) => (
  <div className="space-y-6">
    {/* Hardest Questions */}
    <div className="brutalist-card p-5">
      <h3 className="font-bold text-base mb-1 flex items-center gap-2">
        <TrendingDown className="w-4 h-4 text-red-500" /> Hardest Questions
      </h3>
      <p className="text-xs text-[#4A4A4A] mb-4">Questions with the lowest accuracy rates</p>
      <div className="space-y-2">
        {(data.hardest_questions || []).map((q, i) => (
          <div
            key={i}
            data-testid={`hardest-q-${i}`}
            className="p-3 bg-white rounded-lg border border-[#0A0A0A] flex flex-col sm:flex-row sm:items-center justify-between gap-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium line-clamp-2">{q.question}</p>
              <div className="flex gap-1.5 mt-1">
                <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">W{q.week_number}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">{q.type}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${DIFF_COLORS[q.difficulty]?.bg || "bg-[#F0EFEB]"}`}>
                  {q.difficulty}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <div className={`font-bold text-sm ${q.accuracy < 50 ? "text-red-600" : "text-[#0A0A0A]"}`}>
                  {q.accuracy}%
                </div>
                <div className="text-[10px] text-[#4A4A4A]">{q.total_attempts} attempts</div>
              </div>
              {q.avg_time_seconds > 0 && (
                <div className="text-right">
                  <div className="font-bold text-sm">{q.avg_time_seconds}s</div>
                  <div className="text-[10px] text-[#4A4A4A]">avg time</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Easiest Questions */}
    <div className="brutalist-card p-5">
      <h3 className="font-bold text-base mb-1 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-green-500" /> Easiest Questions
      </h3>
      <p className="text-xs text-[#4A4A4A] mb-4">Questions with the highest accuracy rates</p>
      <div className="space-y-2">
        {(data.easiest_questions || []).map((q, i) => (
          <div
            key={i}
            data-testid={`easiest-q-${i}`}
            className="p-3 bg-white rounded-lg border border-[#0A0A0A] flex flex-col sm:flex-row sm:items-center justify-between gap-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium line-clamp-2">{q.question}</p>
              <div className="flex gap-1.5 mt-1">
                <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">W{q.week_number}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">{q.type}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${DIFF_COLORS[q.difficulty]?.bg || "bg-[#F0EFEB]"}`}>
                  {q.difficulty}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <div className="font-bold text-sm text-green-600">{q.accuracy}%</div>
                <div className="text-[10px] text-[#4A4A4A]">{q.total_attempts} attempts</div>
              </div>
              {q.avg_time_seconds > 0 && (
                <div className="text-right">
                  <div className="font-bold text-sm">{q.avg_time_seconds}s</div>
                  <div className="text-[10px] text-[#4A4A4A]">avg time</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MistakesTab = ({ data }) => (
  <div className="space-y-6">
    <div className="brutalist-card p-5">
      <h3 className="font-bold text-base mb-1 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-orange-500" /> Common Mistakes
      </h3>
      <p className="text-xs text-[#4A4A4A] mb-4">
        Questions where students struggle most, with their most frequent wrong answers
      </p>
      {(data.common_mistakes || []).length === 0 ? (
        <p className="text-sm text-[#4A4A4A] text-center py-8">
          No significant mistake patterns detected yet. Need more submissions for analysis.
        </p>
      ) : (
        <div className="space-y-4">
          {data.common_mistakes.map((m, i) => (
            <div key={i} data-testid={`mistake-${i}`} className="p-4 bg-[#FFAEBC]/20 rounded-lg border-2 border-[#FFAEBC]">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-medium text-sm">{m.question}</p>
                  <div className="flex gap-1.5 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">Week {m.week}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#F0EFEB] rounded">{m.difficulty}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-red-600">{m.accuracy}%</div>
                  <div className="text-[10px] text-[#4A4A4A]">{m.attempts} attempts</div>
                </div>
              </div>
              {m.common_wrong_answers && m.common_wrong_answers.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-[#4A4A4A] mb-1.5">Most common wrong answers:</div>
                  <div className="space-y-1">
                    {m.common_wrong_answers.map((wa, j) => (
                      <div key={j} className="flex items-center justify-between bg-white px-3 py-1.5 rounded text-sm border">
                        <span className="text-red-700">"{wa.answer}"</span>
                        <span className="text-xs text-[#4A4A4A]">{wa.count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);


const DemoBadge = ({ label, value, color = "bg-[#D6E6FA]" }) => (
  <div className={`p-3 rounded-lg border-2 border-[#0A0A0A] text-center ${color}`}>
    <div className="text-[10px] uppercase tracking-wider text-[#4A4A4A] mb-0.5">{label}</div>
    <div className="font-bold text-lg">{value}</div>
  </div>
);

const DataInsightsTab = ({ data }) => {
  if (!data || data.total_sessions === 0) {
    return (
      <div className="brutalist-card p-12 text-center">
        <Shield className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
        <h2 className="text-xl font-bold mb-2">No Anonymous Data Yet</h2>
        <p className="text-[#4A4A4A] text-sm">
          Anonymous behavioral data will appear here once students start assignment sessions.
          <br />All data is FERPA/COPPA compliant — no PII is ever collected.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-insights-tab">
      {/* Privacy Notice */}
      <div className="flex items-center gap-3 p-3 bg-[#D6E6FA]/50 rounded-lg border border-[#3C8DE4]">
        <Shield className="w-5 h-5 text-[#3C8DE4] shrink-0" />
        <p className="text-xs text-[#4A4A4A]">
          All data is anonymous (UUID session IDs only). No PII is collected or stored.
          {!data.k_anonymity_check && (
            <span className="text-red-600 font-bold ml-1">
              Warning: Some demographic groups have fewer than 10 sessions — export restricted.
            </span>
          )}
          {data.k_anonymity_check && (
            <span className="text-green-700 font-bold ml-1">K-anonymity check passed (k≥10).</span>
          )}
        </p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Sessions" value={data.total_sessions} />
        <StatCard icon={Target} label="Completion Rate" value={`${data.completion_rate}%`} color="bg-[#FBE7C6]" />
        <StatCard
          icon={Zap}
          label="Question Accuracy"
          value={`${data.question_accuracy}%`}
          sub={`${data.total_question_attempts} attempts`}
          color="bg-[#FFAEBC]"
        />
        <StatCard
          icon={Clock}
          label="Avg Response"
          value={data.avg_response_time_sec > 0 ? `${data.avg_response_time_sec}s` : "N/A"}
          sub={`${data.hint_usage_rate}% hint usage`}
        />
      </div>

      {/* Demographics */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* By Age Range */}
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4">Sessions by Age Range</h3>
          <div className="grid grid-cols-5 gap-2">
            {["6-8", "9-11", "12-14", "15-17", "18+"].map((range) => (
              <DemoBadge
                key={range}
                label={range}
                value={data.by_age_range?.[range] || 0}
                color={data.by_age_range?.[range] ? "bg-[#D6E6FA]" : "bg-[#F0EFEB]"}
              />
            ))}
          </div>
        </div>

        {/* By Grade Band */}
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4">Sessions by Grade Band</h3>
          <div className="grid grid-cols-2 gap-2">
            {["Elementary", "Middle School", "High School", "Adult"].map((band) => (
              <DemoBadge
                key={band}
                label={band}
                value={data.by_grade_band?.[band] || 0}
                color={data.by_grade_band?.[band] ? "bg-[#FBE7C6]" : "bg-[#F0EFEB]"}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Behavioral Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="brutalist-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">Avg Session Length</div>
          <div className="text-2xl font-extrabold">
            {data.avg_session_length_sec > 60
              ? `${Math.round(data.avg_session_length_sec / 60)}m`
              : `${data.avg_session_length_sec}s`}
          </div>
        </div>
        <div className="brutalist-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">Hint Usage Rate</div>
          <div className="text-2xl font-extrabold">{data.hint_usage_rate}%</div>
        </div>
        <div className="brutalist-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">Skip Rate</div>
          <div className="text-2xl font-extrabold">{data.skip_rate}%</div>
        </div>
      </div>

      {/* Device Type */}
      {Object.keys(data.by_device_type || {}).length > 0 && (
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4">Sessions by Device</h3>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(data.by_device_type).map(([device, count]) => (
              <DemoBadge key={device} label={device} value={count} />
            ))}
          </div>
        </div>
      )}

      {/* By Skill Area */}
      {Object.keys(data.by_skill_area || {}).length > 0 && (
        <div className="brutalist-card p-5">
          <h3 className="font-bold text-base mb-4">Sessions by Skill Area</h3>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(data.by_skill_area).map(([skill, count]) => (
              <DemoBadge key={skill} label={skill} value={count} color="bg-[#FBE7C6]" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminAnalytics;
